import axios from 'axios';
import toast from 'react-hot-toast';
import { useAuthStore } from '../store/authStore';

const API_BASE = process.env.REACT_APP_API_BASE_URL || '/api/v1';

const api = axios.create({
  baseURL:         API_BASE,
  withCredentials: true,
  headers:         { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config) => {
  const token = useAuthStore.getState().accessToken;
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

let isRefreshing       = false;
let failedQueue        = [];
let lastRateLimitToast = 0;

function processQueue(error, token = null) {
  failedQueue.forEach((p) => (error ? p.reject(error) : p.resolve(token)));
  failedQueue = [];
}

api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const original = error.config;

    if (error.response?.status === 401 && !original._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          original.headers.Authorization = `Bearer ${token}`;
          return api(original);
        });
      }

      original._retry = true;
      isRefreshing = true;

      try {
        const res = await axios.post(`${API_BASE}/auth/refresh`, {}, { withCredentials: true });
        const { accessToken } = res.data.data;
        useAuthStore.getState().setAccessToken(accessToken);
        processQueue(null, accessToken);
        original.headers.Authorization = `Bearer ${accessToken}`;
        return api(original);
      } catch (err) {
        processQueue(err, null);
        useAuthStore.getState().logout();
        window.location.href = '/login';
        return Promise.reject(err);
      } finally {
        isRefreshing = false;
      }
    }

    const status  = error.response?.status;
    const message = error.response?.data?.message || 'Something went wrong';

    if (status === 429) {
      const now = Date.now();
      if (now - lastRateLimitToast > 15_000) {
        lastRateLimitToast = now;
        toast.error('Too many requests — please wait a moment.');
      }
    } else if (status !== 401) {
      toast.error(message);
    }
    return Promise.reject(error);
  }
);

export default api;
